import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { queryKeys } from '@/api/queryKeys'
import { markAdvicePrepPaymentSucceeded } from './paywall/paymentRequired'
import { fetchBillingOverview, isAdvicePrepSubscription, type BillingSubscription } from '@/api/billing'
import { fetchPlatformConnections } from '@/features/platform/api'
import type { PlatformConnectionRow, PlatformType } from '@/features/platform/types'
import { permissions, UserTypes } from '@/types/authorization'
import { useAuthSession } from '@/lib/session'
import { ADVICEPREP_BILLABLE_PLATFORM, ADVICEPREP_TRIAL_CONNECTIONS } from './pricing'
import {
  cancelAdvicePrepSubscription,
  fetchAdvicePrepOutstandingInvoice,
  payAdvicePrepOutstandingInvoice,
  resumeAdvicePrepSubscription,
  subscribeAdvicePrep,
  subscribeAdvicePrepNow,
  validateAdvicePrepCoupon,
} from './api'

/**
 * There is no AdvicePrep read endpoint (spec 5.3). The shared
 * `user/ui/stripe/subscription` overview already carries it, identified by
 * price nickname, so the Billing page picks its row out of that.
 */
export function findAdvicePrepSubscription(
  subscriptions: BillingSubscription[] | undefined
): BillingSubscription | null {
  // A live row wins over an earlier dead one. The response is not ordered, and
  // cancel-then-resubscribe is a path this product has, so a client can hold an
  // ended AdvicePrep row ahead of the one they are actually on. Taking the first
  // match would read a trialing client as not trialing and switch the whole
  // connection cap off silently.
  return (
    subscriptions?.find((subscription) => isAdvicePrepSubscription(subscription) && isSubscribed(subscription)) ??
    subscriptions?.find(isAdvicePrepSubscription) ??
    null
  )
}

/**
 * Spec section 7 — the paywall reason rides the authz context alongside the
 * permissions it explains, so the two cannot disagree. Do not move it onto a
 * separate request.
 */
export function useAdvicePrepPaywall() {
  return useAuthorization().advicePrepPaywall
}

/**
 * Completing payment or fixing a card lifts the paywall server-side, so the
 * permission set has to be reloaded or the nav and routes stay locked until
 * the next login.
 */
function useAdvicePrepInvalidation(clientId: string) {
  const queryClient = useQueryClient()
  const { refresh } = useAuthorization()

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.overview(clientId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.billing.access(clientId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.adviceprep.liveSubscription(clientId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.adviceprep.trialState(clientId) }),
    ])
    await refresh()
  }
}

/**
 * past_due counts: the subscription still exists and is still metered, and that
 * is exactly when an admin needs to take a connection off it.
 */
const SUBSCRIBED_STATUSES: ReadonlySet<BillingSubscription['status']> = new Set([
  'active',
  'trialing',
  'past_due',
])

/**
 * A pending cancellation arrives as canceled with the period still to run, and
 * it bills that period. A known period end is required because isExpired is
 * also false when the date is missing, which would let an ended one through.
 */
function isRunningOutItsPeriod(subscription: BillingSubscription) {
  return subscription.status === 'canceled' && !subscription.isExpired && !!subscription.currentPeriodEnd
}

function isSubscribed(subscription: BillingSubscription) {
  return SUBSCRIBED_STATUSES.has(subscription.status) || isRunningOutItsPeriod(subscription)
}

/**
 * The one definition of the sessionless AdvicePrep read, shared by every hook
 * that needs it so two of them cannot end up under one key with different
 * queryFns — which silently decides whose data the cache keeps.
 *
 * Not billing.overview: this fetch has no session, so its product visibility
 * flags would overwrite the real ones Billing caches under that key. Billing
 * also feeds `platformType` in from client detail, which is not on the session
 * at all, so the two fetches can never be made equivalent. AdvicePrep is
 * visible whatever the session carries, so the row this needs survives an
 * empty one.
 */
function advicePrepOverviewQuery(clientId: string, queryKey: readonly unknown[]) {
  return {
    queryKey,
    queryFn: () => fetchBillingOverview(clientId, {}),
  }
}

export function useAdvicePrepSubscriptionCheck(clientId: string, enabled: boolean) {
  const { data, isPending, isError } = useQuery({
    ...advicePrepOverviewQuery(clientId, queryKeys.adviceprep.liveSubscription(clientId)),
    enabled: !!clientId && enabled,
    // Retrying leaves the caller unable to tell "checking" from "no subscription"
    // for seconds, which is long enough to save without the field.
    retry: false,
    // `some`, not findAdvicePrepSubscription: a client with an ended AdvicePrep
    // row ahead of a live one still has a live one.
    select: (overview) =>
      overview.subscriptions.some(
        (subscription) => isAdvicePrepSubscription(subscription) && isSubscribed(subscription)
      ),
  })
  return { hasSubscription: data === true, isChecking: enabled && isPending, isError }
}

/**
 * On the AdvicePrep meter means supportAdvisePrep plus a credential; anything
 * else is not included. Neither says why a connection is off the meter, so the
 * UI does not claim a reason. `unknown` is kept apart from
 * `not_included` only so a missing flag cannot be counted as a real zero.
 */
export type AdvicePrepConnectionBilling = 'billed' | 'not_included' | 'unknown'

export type AdvicePrepTrialConnection = {
  id: string
  name: string
  type: PlatformType
  billable: boolean | undefined
  billing: AdvicePrepConnectionBilling
}

const NO_CONNECTIONS: readonly AdvicePrepTrialConnection[] = []

function hasMeterCredential(row: PlatformConnectionRow) {
  return !!row.hasRefreshToken || !!row.hasApiKey || !!row.apiKey
}

// Mirrors the API's meter: opted in and holding a refresh token or API key.
// Ownership and deletion are already settled by the grid's per-client query.
function toTrialConnection(row: PlatformConnectionRow): AdvicePrepTrialConnection {
  const optedIn = row.supportAdvisePrep
  const meterable = row.type === ADVICEPREP_BILLABLE_PLATFORM
  const billable = optedIn === undefined ? undefined : optedIn && hasMeterCredential(row)

  // A flag only goes unknown on a connection that could have carried one. An
  // absent flag on, say, a Karbon row is not a gap — it was never meterable.
  const billing: AdvicePrepConnectionBilling =
    billable === true ? 'billed' : billable === false || !meterable ? 'not_included' : 'unknown'

  return { id: row.id, name: row.name, type: row.type, billable, billing }
}

/**
 * The trial's free-connection allowance, for the screens that add connections.
 *
 * Only connections on the AdvicePrep meter count, so a client's pre-existing
 * platforms stay exempt and never use the allowance up. The count comes from
 * the grid's supportAdvisePrep flag rather than a billing field, so it moves
 * the moment a connection is added or taken off the meter.
 *
 * Fails open. A client that is not trialing, or whose data has not loaded, is
 * never locked: wrongly blocking a paying customer is worse than letting a
 * third connection through to the server, which rejects it anyway.
 */
type AdvicePrepTrialLimitOptions = {
  enabled?: boolean
  /**
   * The AdvicePrep row, when the caller already has it. Passing it — `null`
   * included — stops this hook re-fetching the overview, which matters beyond
   * the saved request: that query runs with `retry: false`, so one transient
   * failure would otherwise read as "not trialing" and quietly switch the
   * allowance off on a page that was handed the answer.
   */
  subscription?: BillingSubscription | null
}

export function useAdvicePrepTrialLimit(clientId: string, options: AdvicePrepTrialLimitOptions = {}) {
  const { enabled = true } = options
  const subscriptionSupplied = 'subscription' in options
  // Platform/ui/list/grid 401s for a user outside its filter, and a surviving
  // 401 sends httpClient to the login screen. This hook runs on screens that do
  // not otherwise read platforms, so without the permission gate a background
  // count would sign those users out. viewPlatform is held by every role the
  // endpoint's own filter admits.
  const { permissions: userPermissions } = useAuthorization()
  const session = useAuthSession()
  const canReadPlatforms = enabled && !!clientId && userPermissions.has(permissions.viewPlatform)
  // A DI admin adds connections on a client's behalf and is not spending that
  // client's trial allowance, so the cap never gates them.
  const isDiAdmin = session?.currentUserTypeId === UserTypes.Admin

  const { data: fetchedSubscription } = useQuery({
    ...advicePrepOverviewQuery(clientId, queryKeys.adviceprep.trialState(clientId)),
    enabled: canReadPlatforms && !subscriptionSupplied,
    select: (overview) => findAdvicePrepSubscription(overview.subscriptions),
    retry: false,
  })

  const subscription = subscriptionSupplied ? options.subscription : fetchedSubscription
  const isTrialing = subscription?.status === 'trialing'

  const { connections, metered: used } = useAdvicePrepMeteredConnections(clientId, canReadPlatforms && isTrialing)
  const countKnown = used !== undefined
  const limit = ADVICEPREP_TRIAL_CONNECTIONS

  // The fact, and the gate derived from it. They differ for a DI admin, who
  // sees a spent allowance but is still allowed to add.
  const allowanceSpent = isTrialing && used !== undefined && used >= limit

  return {
    isTrialing,
    countKnown,
    connections,
    used,
    limit,
    trialEnd: subscription?.trialEnd ?? null,
    allowanceSpent,
    atLimit: allowanceSpent && !isDiAdmin,
  }
}

/**
 * Connections on the AdvicePrep meter right now, by the same rule the API meters
 * on. Invoice quantities are connection-days, so they cannot give this.
 */
export function useAdvicePrepMeteredConnections(clientId: string, enabled: boolean) {
  const { permissions: userPermissions } = useAuthorization()
  // Deliberately the unfiltered list: the meter counts every billable connection,
  // not the ones matching whatever filter the grid has set. retry: false because a
  // client type the endpoint's filter does not handle answers 500, and retrying
  // only repeats it.
  const { data: connections } = useQuery({
    queryKey: queryKeys.platform.list({ clientId }),
    queryFn: () => fetchPlatformConnections({ clientId }),
    enabled: enabled && !!clientId && userPermissions.has(permissions.viewPlatform),
    select: (rows) => rows.map(toTrialConnection),
    retry: false,
  })

  // One meterable row without a flag makes the whole count unreliable, so the
  // figure is withheld rather than shown low. Rows that could never be metered
  // are not gaps, or a client with a single Karbon connection would hide the
  // counter forever. Nothing to count is still a known zero.
  const flagMissing = connections?.some((connection) => connection.billing === 'unknown') ?? true
  const metered =
    connections && !flagMissing ? connections.filter((connection) => connection.billable).length : undefined

  return { connections: connections ?? NO_CONNECTIONS, metered }
}

/** Spec 5.9 — what the customer owes, so the pay button can show the figure. */
export function useAdvicePrepOutstandingInvoice(clientId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.adviceprep.outstandingInvoice(clientId),
    queryFn: () => fetchAdvicePrepOutstandingInvoice(clientId),
    enabled: !!clientId && enabled,
    retry: false,
  })
}

/** Spec 5.9 — settles the invoice, which is what actually clears `past_due`. */
export function usePayAdvicePrepInvoiceMutation(clientId: string) {
  const invalidate = useAdvicePrepInvalidation(clientId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => payAdvicePrepOutstandingInvoice({ clientId }),
    onSuccess: async (result) => {
      if (!result.isSuccessful) return
      markAdvicePrepPaymentSucceeded()
      await queryClient.invalidateQueries({ queryKey: queryKeys.adviceprep.outstandingInvoice(clientId) })
      await invalidate()
    },
  })
}

/**
 * Checked before the subscribe call, and before a card is collected: a code the
 * server rejects must never reach a flow that charges. Not a query — the result
 * is only good at the moment it is asked for, and caching it would let an
 * expired code look valid.
 */
export function useValidateAdvicePrepCouponMutation() {
  return useMutation({ mutationFn: (code: string) => validateAdvicePrepCoupon(code) })
}

/** D-14 — the Billing upsell subscribes here; it never routes into the signup wizard. */
export function useSubscribeAdvicePrepMutation(clientId: string) {
  const invalidate = useAdvicePrepInvalidation(clientId)
  return useMutation({
    mutationFn: (input: { promoCode?: string } = {}) => subscribeAdvicePrep({ clientId, ...input }),
    onSuccess: (result) => {
      if (!result.isSuccessful) return
      markAdvicePrepPaymentSucceeded()
      return invalidate()
    },
  })
}

/** Ends a promo-code trial early. The trial panel, its free connections and the paywall all follow the new status. */
export function useSubscribeAdvicePrepNowMutation(clientId: string) {
  const invalidate = useAdvicePrepInvalidation(clientId)
  return useMutation({
    mutationFn: () => subscribeAdvicePrepNow({ clientId }),
    onSuccess: (result) => {
      if (!result.isSuccessful) return
      markAdvicePrepPaymentSucceeded()
      return invalidate()
    },
  })
}

export function useCancelAdvicePrepMutation(clientId: string) {
  const invalidate = useAdvicePrepInvalidation(clientId)
  return useMutation({
    mutationFn: () => cancelAdvicePrepSubscription({ clientId }),
    onSuccess: (result) => {
      if (result.isSuccessful) return invalidate()
    },
  })
}

export function useResumeAdvicePrepMutation(clientId: string) {
  const invalidate = useAdvicePrepInvalidation(clientId)
  return useMutation({
    mutationFn: () => resumeAdvicePrepSubscription({ clientId }),
    onSuccess: (result) => {
      if (!result.isSuccessful) return
      markAdvicePrepPaymentSucceeded()
      return invalidate()
    },
  })
}
